import { z } from "zod";
import { AccountId, WebhookEndpointId } from "./ids.ts";

/**
 * Closed set of domain events an outbound webhook endpoint may subscribe to.
 * These mirror the `transaction.*` events the realtime bus publishes — the
 * ones meaningful to a platform integrator. High-volume internal events
 * (`wallet.balance-changed`) and the in-app approval push
 * (`wallet.approval-requested`) are deliberately NOT webhook-eligible; extend
 * this set (and the producer's dispatch map) when a new integrator-facing
 * event is added, never by overloading an existing one.
 */
export const WebhookEventType = z.enum([
  // A transaction posted to the ledger (a buy mint, a transfer, a spend).
  "transaction.posted",
  // A posted transaction was reversed (e.g. a chargeback burn).
  "transaction.reversed",
]);
export type WebhookEventType = z.output<typeof WebhookEventType>;

/**
 * Lifecycle of a webhook endpoint. `active` endpoints receive deliveries;
 * `disabled` is a soft off-switch the owner can flip (kept, not deleted, so
 * the delivery history stays attributable). Hard removal is a separate delete.
 */
export const WebhookEndpointStatus = z.enum(["active", "disabled"]);
export type WebhookEndpointStatus = z.output<typeof WebhookEndpointStatus>;

/**
 * A registered outbound webhook endpoint — the management view, never the
 * signing secret. The secret is generated and shown exactly once at
 * registration (like an API key's raw value); at rest it's read only by the
 * delivery producer to HMAC-sign each payload, never returned to a list/get.
 */
export const WebhookEndpoint = z.object({
  id: WebhookEndpointId,
  accountId: AccountId,
  /** Destination the signed POST is delivered to. HTTPS in practice. */
  url: z.string().url(),
  /** Owner-facing label for the dashboard, e.g. "prod fulfilment". */
  label: z.string().min(1).max(80),
  /** Non-empty set of subscribed event types — no duplicates. */
  events: z
    .array(WebhookEventType)
    .min(1)
    .refine((e) => new Set(e).size === e.length, "events must be unique"),
  status: WebhookEndpointStatus,
  // Strict `z.date()` — coercion belongs at the api / persistence boundary.
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type WebhookEndpoint = z.output<typeof WebhookEndpoint>;

/**
 * Disposition of one webhook delivery attempt (#25: "delivery logged for
 * dashboard"). Mirrors the worker's outcome classification:
 * - `delivered` — a 2xx response; done.
 * - `retryable` — 5xx / 408 / 429 / transport error; the worker retries until
 *   the policy is exhausted (the last such record is the final state).
 * - `permanent` — a non-retryable 4xx or a blocked host; dropped after this attempt.
 */
export const WebhookDeliveryOutcome = z.enum(["delivered", "retryable", "permanent"]);
export type WebhookDeliveryOutcome = z.output<typeof WebhookDeliveryOutcome>;

/**
 * One recorded webhook delivery attempt — the dashboard read model. One row is
 * written per attempt, so an event with retries shows its full timeline. Carries
 * no secret and a redacted url (userinfo/query stripped at the worker).
 */
export const WebhookDelivery = z.object({
  id: z.string(),
  accountId: AccountId,
  endpointId: WebhookEndpointId,
  /** Correlates every attempt of one logical event (e.g. the transaction id). */
  eventId: z.string(),
  eventType: WebhookEventType,
  /** 1-based attempt number this record describes. */
  attempt: z.number().int().positive(),
  outcome: WebhookDeliveryOutcome,
  /** HTTP status received, or null on a transport error / blocked host. */
  statusCode: z.number().int().nullable(),
  /** Wall-clock duration of the attempt, milliseconds. */
  durationMs: z.number().int().nonnegative(),
  /** Short failure reason; null on a delivered attempt. */
  error: z.string().nullable(),
  /** When the attempt started. */
  createdAt: z.date(),
});
export type WebhookDelivery = z.output<typeof WebhookDelivery>;
